import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BACKEND_BASE_URL = API_BASE_URL;
const createEmptyUpdateFiles = () => ({ image_path_en: null, image_path_ar: null });

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${BACKEND_BASE_URL}/${imagePath.startsWith("uploads/") ? imagePath : `uploads/${imagePath}`}`;
};

function Icons() {
  const [icons, setIcons] = useState([]);
  const [iconCount, setIconCount] = useState(0);
  const [updateFiles, setUpdateFiles] = useState(createEmptyUpdateFiles);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [editingIcon, setEditingIcon] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteIconId, setDeleteIconId] = useState(null);
  const [addError, setAddError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [imageError, setImageError] = useState("");
  const [addSuccessMessage, setAddSuccessMessage] = useState("");
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");

  const isValidImageExtension = (fileName) => {
    const validExtensions = [".webp", ".gif", ".png", ".jpg", ".jpeg"];
    const fileExtension = fileName
      .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();
    return validExtensions.includes(`.${fileExtension}`);
  };

  const resetAddMessages = () => {
    setAddError("");
    setAddSuccessMessage("");
    setImageError("");
  };

  const resetUpdateMessages = () => {
    setUpdateError("");
    setUpdateSuccessMessage("");
    setImageError("");
  };

  const fetchIcons = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/icons/list`);
      if (response.data.success) {
        setIcons(response.data.data || []);
        setIconCount((response.data.data || []).length);
      } else {
        console.error("API not successful:", response.data.errors);
      }
    } catch (error) {
      console.error("Error fetching icons:", error);
    }
  };

  const addIcon = async () => {
    resetAddMessages();

    const enInput = document.getElementById("newIconimage_path_en");
    const arInput = document.getElementById("newIconimage_path_ar");
    const enFile = enInput?.files?.[0] || null;
    const arFile = arInput?.files?.[0] || null;

    if (!enFile || !arFile) {
      setAddError("Please choose both English and Arabic banner images first.");
      return;
    }

    if (!isValidImageExtension(enFile.name) || !isValidImageExtension(arFile.name)) {
      setImageError("Invalid file extension. Please upload .WEBP, .GIF, .PNG, .JPG, or .JPEG");
      return;
    }

    const formData = new FormData();
    formData.append("image_path_en", enFile);
    formData.append("image_path_ar", arFile);

    try {
      const response = await axios.post(`${BACKEND_BASE_URL}/icons/add`, formData);
      if (response.data.success) {
        setAddSuccessMessage("Banner added successfully.");
        fetchIcons();
        if (enInput) enInput.value = "";
        if (arInput) arInput.value = "";
      } else {
        setAddError(`Failed to add banner: ${response.data.errors || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error adding icon:", error);
      setAddError("Error occurred while adding the banner.");
    }
  };

  const handleUpdate = (icon) => {
    resetUpdateMessages();
    setEditingIcon(icon);
    setUpdateFiles(createEmptyUpdateFiles());
    setShowUpdateForm(true);
  };

  const closeUpdateForm = () => {
    setEditingIcon(null);
    setUpdateFiles(createEmptyUpdateFiles());
    setShowUpdateForm(false);
    resetUpdateMessages();
  };

  const handleUpdateFileChange = (fieldName, file) => {
    setUpdateFiles((prev) => ({
      ...prev,
      [fieldName]: file || null,
    }));
  };

  const updateIcon = async (id) => {
    resetUpdateMessages();

    const hasEnUpdate = Boolean(updateFiles.image_path_en);
    const hasArUpdate = Boolean(updateFiles.image_path_ar);

    if (!hasEnUpdate && !hasArUpdate) {
      setUpdateError("Please choose at least one new image to update.");
      return;
    }

    if (
      (hasEnUpdate && !isValidImageExtension(updateFiles.image_path_en.name)) ||
      (hasArUpdate && !isValidImageExtension(updateFiles.image_path_ar.name))
    ) {
      setImageError("Invalid file extension. Please upload .WEBP, .GIF, .PNG, .JPG, or .JPEG");
      return;
    }

    const formData = new FormData();
    if (hasEnUpdate) {
      formData.append("image_path_en", updateFiles.image_path_en);
    }
    if (hasArUpdate) {
      formData.append("image_path_ar", updateFiles.image_path_ar);
    }

    try {
      const response = await axios.put(`${BACKEND_BASE_URL}/icons/update/${id}`, formData);
      if (response.data.success) {
        setUpdateSuccessMessage("Banner updated successfully.");
        fetchIcons();
        setTimeout(() => {
          closeUpdateForm();
        }, 800);
      } else {
        setUpdateError(`Failed to update banner: ${response.data.errors || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error updating icon:", error);
      setUpdateError("Error occurred while updating the banner.");
    }
  };

  const deleteIcon = (id) => {
    setDeleteIconId(id);
    setShowDeletePopup(true);
  };

  const performDelete = async () => {
    try {
      const response = await axios.delete(`${BACKEND_BASE_URL}/icons/delete/${deleteIconId}`);
      if (response.data.success) {
        setIcons((prev) => prev.filter((icon) => icon.id !== deleteIconId));
        setIconCount((prev) => Math.max(0, prev - 1));
        setShowDeletePopup(false);
      } else {
        console.error("Failed to delete icon");
      }
    } catch (error) {
      console.error("Error deleting icon:", error);
    }
  };

  useEffect(() => {
    fetchIcons();
  }, []);

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Banner</CardTitle>
              </CardHeader>

              <CardBody>
                <Form>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <label>English Image:</label>
                        <Input
                          type="file"
                          id="newIconimage_path_en"
                          accept=".webp,.gif,.png,.jpg,.jpeg"
                          onChange={() => {
                            setAddError("");
                            setAddSuccessMessage("");
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label>Arabic Image:</label>
                        <Input
                          type="file"
                          id="newIconimage_path_ar"
                          accept=".webp,.gif,.png,.jpg,.jpeg"
                          onChange={() => {
                            setAddError("");
                            setAddSuccessMessage("");
                          }}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <Button className="btn-round" color="primary" onClick={addIcon}>
                        Add Banner
                      </Button>

                      {addSuccessMessage && (
                        <div
                          className="alert alert-success"
                          role="alert"
                          style={{ width: "50%", margin: "10px auto", textAlign: "center" }}
                        >
                          {addSuccessMessage}
                        </div>
                      )}

                      {addError && (
                        <div
                          className="alert alert-danger"
                          role="alert"
                          style={{ width: "50%", margin: "10px auto", textAlign: "center" }}
                        >
                          {addError}
                        </div>
                      )}

                      {imageError && (
                        <div style={{ color: "red", textAlign: "center" }}>
                          {imageError}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Form>

                <CardFooter>
                  <div>Total Banner: {iconCount}</div>
                </CardFooter>

                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>Image (EN)</th>
                      <th>Image (AR)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {icons.map((icon) => (
                      <tr key={icon.id}>
                        <td>
                          {icon.image_path_en && (
                            <img
                              src={resolveImageUrl(icon.image_path_en)}
                              alt="Banner EN"
                              style={{ maxWidth: "120px" }}
                            />
                          )}
                        </td>
                        <td>
                          {icon.image_path_ar && (
                            <img
                              src={resolveImageUrl(icon.image_path_ar)}
                              alt="Banner AR"
                              style={{ maxWidth: "120px" }}
                            />
                          )}
                        </td>
                        <td>
                          <Button color="success" onClick={() => handleUpdate(icon)}>
                            Update
                          </Button>{" "}
                          <Button color="danger" onClick={() => deleteIcon(icon.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>

              {showUpdateForm && editingIcon && (
                <CardFooter>
                  <Form>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <label>Current English Image:</label>
                          {(editingIcon.image_path_en || editingIcon.image_path) && (
                            <div>
                              <img
                                src={resolveImageUrl(editingIcon.image_path_en || editingIcon.image_path)}
                                alt="Current Banner EN"
                                style={{ maxWidth: "200px", marginBottom: "10px" }}
                              />
                            </div>
                          )}
                          <label>Update English Image (optional):</label>
                          <Input
                            type="file"
                            accept=".webp,.gif,.png,.jpg,.jpeg"
                            onChange={(e) => handleUpdateFileChange("image_path_en", e.target.files?.[0])}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <label>Current Arabic Image:</label>
                          {(editingIcon.image_path_ar || editingIcon.image_path) && (
                            <div>
                              <img
                                src={resolveImageUrl(editingIcon.image_path_ar || editingIcon.image_path)}
                                alt="Current Banner AR"
                                style={{ maxWidth: "200px", marginBottom: "10px" }}
                              />
                            </div>
                          )}
                          <label>Update Arabic Image (optional):</label>
                          <Input
                            type="file"
                            accept=".webp,.gif,.png,.jpg,.jpeg"
                            onChange={(e) => handleUpdateFileChange("image_path_ar", e.target.files?.[0])}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="12">
                        <Button className="btn-round" color="info" onClick={() => updateIcon(editingIcon.id)}>
                          Update Banner
                        </Button>{" "}
                        <Button className="btn-round" color="secondary" onClick={closeUpdateForm}>
                          Cancel
                        </Button>

                        {updateSuccessMessage && (
                          <div
                            className="alert alert-success"
                            role="alert"
                            style={{ width: "50%", margin: "10px auto", textAlign: "center" }}
                          >
                            {updateSuccessMessage}
                          </div>
                        )}
                        {updateError && (
                          <div
                            className="alert alert-danger"
                            role="alert"
                            style={{ width: "50%", margin: "10px auto", textAlign: "center" }}
                          >
                            {updateError}
                          </div>
                        )}
                        {imageError && (
                          <div style={{ color: "red", textAlign: "center" }}>
                            {imageError}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>

        <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)}>
          <ModalHeader toggle={() => setShowDeletePopup(false)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>Are you sure you want to delete this banner?</ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={performDelete}>
              Delete
            </Button>
            <Button color="secondary" onClick={() => setShowDeletePopup(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
}

export default Icons;
